
import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  User, 
  Shield, 
  Code,
  Lightbulb
} from 'lucide-react';
import SEO from '@/components/SEO';

// Create accordion components for the content sections
const AccordionItem = ({ 
  question, 
  answer, 
  isOpen, 
  toggleOpen 
}: { 
  question: string; 
  answer: string;
  isOpen: boolean;
  toggleOpen: () => void;
}) => {
  return (
    <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
      <button 
        className={`w-full flex items-center justify-between p-5 text-left ${isOpen ? 'bg-frikopplad-cream' : 'bg-white'}`}
        onClick={toggleOpen}
      >
        <h3 className="text-lg font-medium text-frikopplad-navy">{question}</h3>
        <span className="text-2xl">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      
      {isOpen && (
        <div className="p-5 bg-white border-t border-gray-200">
          <p className="text-gray-700">{answer}</p>
        </div>
      )}
    </div>
  );
};

// Create a section component for categories
const FAQSection = ({ 
  title, 
  items, 
  icon,
  openItem, 
  setOpenItem 
}: { 
  title: string; 
  items: { question: string; answer: string }[];
  icon: React.ReactNode;
  openItem: string | null;
  setOpenItem: (id: string | null) => void;
}) => {
  return (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-frikopplad-cream p-3 rounded-full text-frikopplad-burgundy">
          {icon}
        </div>
        <h2 className="text-2xl font-bold text-frikopplad-navy">{title}</h2>
      </div>
      <div>
        {items.map((item, index) => (
          <AccordionItem
            key={index}
            question={item.question}
            answer={item.answer}
            isOpen={openItem === `${title}-${index}`}
            toggleOpen={() => setOpenItem(openItem === `${title}-${index}` ? null : `${title}-${index}`)}
          />
        ))}
      </div>
    </div>
  );
};

const FAQ = () => {
  const { t } = useTranslation('faq');
  const [openItem, setOpenItem] = useState<string | null>(null);
  
  // Explicitly type the items to ensure proper typing
  const generalItems = t('categories.general.items', { returnObjects: true }) as { question: string; answer: string }[];
  const technicalItems = t('categories.technical.items', { returnObjects: true }) as { question: string; answer: string }[];
  const opensourceItems = t('categories.opensource.items', { returnObjects: true }) as { question: string; answer: string }[];
  const practicalItems = t('categories.practical.items', { returnObjects: true }) as { question: string; answer: string }[];
  
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="FAQ - FriKopplad"
        description="Find answers to common questions about digital sovereignty, open source technology, and transitioning from proprietary services."
      />
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-frikopplad-cream to-white">
          <div className="container-custom py-16">
            <h1 className="text-4xl md:text-5xl font-bold text-frikopplad-navy mb-6">
              {t('hero.title')}
            </h1>
            <p className="text-xl text-frikopplad-navy opacity-90 max-w-4xl">
              {t('hero.description')}
            </p>
          </div>
        </div>
        
        {/* FAQ Content */}
        <div className="container-custom py-12">
          <div className="max-w-4xl mx-auto">
            {/* General Questions */}
            <FAQSection 
              title={t('categories.general.title')}
              items={generalItems}
              icon={<User size={24} />}
              openItem={openItem}
              setOpenItem={setOpenItem}
            />
            
            {/* Technical Questions */}
            <FAQSection 
              title={t('categories.technical.title')}
              items={technicalItems}
              icon={<Shield size={24} />}
              openItem={openItem}
              setOpenItem={setOpenItem}
            />
            
            {/* Open Source Questions */}
            <FAQSection 
              title={t('categories.opensource.title')}
              items={opensourceItems}
              icon={<Code size={24} />}
              openItem={openItem}
              setOpenItem={setOpenItem}
            />
            
            {/* Practical Questions */}
            <FAQSection 
              title={t('categories.practical.title')}
              items={practicalItems}
              icon={<Lightbulb size={24} />}
              openItem={openItem}
              setOpenItem={setOpenItem}
            />
          </div>
          
          {/* Call to Action section has been removed */}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FAQ;
